import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordHatefulConductPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')
  const [hateSymbolsExamplesOpen, setHateSymbolsExamplesOpen] = useState(false)

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs' ? 'Politika nenávistného chování' : 'Hateful Conduct Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice nenávistného chování Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/hateful-conduct-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://discord.com/safety/hateful-conduct-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord hateful conduct policy available at{' '}
                <a
                  href="https://discord.com/safety/hateful-conduct-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://discord.com/safety/hateful-conduct-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Disclaimer section - always show both languages */}

      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Nenávistné projevy</h3>
              <ul>
                <li>Vyjadřování pohrdání vůči chráněným skupinám</li>
                <li>Dehumanizace jednotlivců</li>
                <li>Šíření negativních stereotypů</li>
                <li>Používání urážlivých výrazů a slurs</li>
                <li>Popírání historických zvěrstev</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Chráněné charakteristiky</h3>
              <ul>
                <li>Věk, kasta, barva pleti, postižení</li>
                <li>Etnicita, pohlaví, genderová identita</li>
                <li>Národnost, rasa, náboženství</li>
                <li>Sexuální orientace, sociální status</li>
                <li>Váha, velikost a další...</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Nenávistné symboly</h3>
              <ul>
                <li>Zakázány jsou loga, vlajky, gesta</li>
                <li>Symboly nadřazenosti skupin</li>
                <li>Výjimky pro vzdělávací účely</li>
                <li>Zákaz glorifikace násilí</li>
                <li>Žádné popírání holocaustu</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <section id="hate-speech" className={styles.section}>
              <h2>
                <strong>Nenávistné projevy</strong>
              </h2>
              <p>
                Discord je místo, kde se lidé scházejí, aby se bavili s přáteli. V rámci našeho cíle
                podporovat přijetí a inkluzivitu zakazujeme nenávistné projevy, diskriminaci a
                předsudky.
              </p>
              <p>
                Nepovolujeme nenávistné chování nebo používání nenávistných projevů na Discordu. To
                zahrnuje používání nenávistných symbolů a popírání historických masových zvěrstev.{' '}
                <strong>Nenávistný projev</strong> definujeme jako jakýkoli výraz, který degraduje,
                hanobí nebo dehumanizuje jednotlivce, podněcuje nepřátelství vůči definovaným
                skupinám nebo propaguje újmu na základě chráněných charakteristik.
              </p>
              <p>
                <strong>Chráněné charakteristiky</strong> na Discordu zahrnují různé aspekty, jako
                jsou věk; kasta; barva pleti; postižení; etnicita; rodinné povinnosti; gender a
                genderová identita; bytová situace; národní původ; rasa; status uprchlíka nebo
                imigranta; náboženská příslušnost; vážná nemoc; pohlaví; sexuální orientace;
                socioekonomický status; zdroj příjmu; status oběti domácího násilí, sexuálního
                násilí nebo stalkingu; stejně jako váha a velikost.
              </p>
              <p>
                Chápeme však, že existují nuance, a můžeme udělit výjimky v případech znovu
                přivlastněného jazyka, satiry, vzdělávacích nebo dokumentárních účelů. Použití
                satiry musí být zřejmé a nepovolíme uživatelům zpětně odvádět vinu tvrzením, že
                jejich prohlášení byla učiněna ironicky nebo jako vtip.
              </p>
            </section>

            <section id="hate-symbols" className={styles.section}>
              <h2>
                <strong>Nenávistné symboly a obrazy</strong>
              </h2>
              <p>
                Používání nebo sdílení nenávistných symbolů nebo obrazů je na Discordu přísně
                zakázáno. Tato politika se vztahuje na všechny obrázky nahrané nebo sdílené na
                Discordu, včetně profilů, kanálů, serverů a aplikací.
              </p>
              <p>
                Nenávistné symboly zahrnují zkratky, čísla, fráze, loga, vlajky, gesta a další různé
                symboly používané k propagaci nebo podněcování nenávisti, výhrůžek, diskriminace
                nebo násilí vůči jiným lidem na základě chráněných charakteristik. Symboly, které
                představují nadřazenost jedné skupiny nad druhou, jsou také zakázány podle této
                politiky.
              </p>
              <p>
                Výjimky však mohou být učiněny v případech, kdy jsou nenávistné symboly sdíleny pro
                vzdělávací, historické, zpravodajské nebo kulturně vhodné a ne-nenávistné účely.
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setHateSymbolsExamplesOpen(!hateSymbolsExamplesOpen)}
                aria-expanded={hateSymbolsExamplesOpen}
                type="button"
              >
                <span>Například nesmíte zveřejňovat, sdílet nebo se zapojovat do:</span>
                <span className={styles.dropdownIcon}>{hateSymbolsExamplesOpen ? '−' : '+'}</span>
              </button>

              {hateSymbolsExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <p>
                    Sdílení jakéhokoli nenávistného symbolu nebo obrazu s některým z následujících
                    záměrů:
                  </p>
                  <ul>
                    <li>
                      Zacílení na jednotlivce, skupinu nebo server na základě jejich chráněných
                      charakteristik jako způsob jejich obtěžování, ohrožování nebo poškozování
                    </li>
                    <li>
                      Vyvolávání strachu, nenávisti, diskriminace nebo násilí vůči chráněné skupině
                    </li>
                    <li>
                      Reprezentování nebo nárokování nadřazenosti jedné chráněné skupiny nad druhou
                    </li>
                    <li>
                      Oslavování násilí vůči jednotlivcům nebo skupinám na základě jejich chráněných
                      charakteristik
                    </li>
                    <li>Popírání existence násilné události (jako je holocaust)</li>
                  </ul>
                </div>
              )}
            </section>

            <section id="how-to-avoid" className={styles.section}>
              <h2>
                <strong>Jak se vyhnout porušení těchto politik</strong>
              </h2>
              <p>
                Nahlaste a opusťte servery věnované výše popsanému obsahu porušujícímu pravidla.
                Discord může podniknout kroky i proti účtům přítomným na takových serverech.
              </p>
              <ul>
                <li>
                  Pokud si nejste jisti, zda nějaký obsah může porušit tyto zásady, nezveřejňujte
                  jej
                </li>
              </ul>
              <p>
                Pokud se ocitnete na serveru, který sdílí tento typ obsahu,{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  nahlaste to
                </a>{' '}
                a tento prostor opusťte
              </p>
            </section>

            <section id="how-to-report" className={styles.section}>
              <h2>
                <strong>Jak nahlásit porušení těchto politik</strong>
              </h2>
              <p>
                Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás a
                širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení zásad
                přímo v aplikaci podle pokynů{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zde
                </a>
                . Pro více informací o těchto politikách se prosím podívejte na naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                <GuidelineTooltip guidelineNumber="4" language={language}>
                  <a href="/discord/guidelines#guideline-4" className={styles.guidelineLink}>
                    #4
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
            <section id="resources" className={styles.section}>
              <h2>Zdroje pomoci</h2>
              <p>
                Pokud jste vy nebo jiný uživatel v nebezpečí, okamžitě kontaktujte orgány činné v
                trestním řízení a dejte jim vědět, co se děje, bez ohledu na informace, které můžete
                poskytnout.
              </p>
            </section>
          </>
        ) : (
          <>
            <section id="hate-speech" className={styles.section}>
              <h2>
                <strong>Hate Speech</strong>
              </h2>
              <p>
                Discord is a place where people come to hang out with friends. As part of our goal
                to promote acceptance and inclusivity, we prohibit hate speech, discrimination, and
                prejudice.
              </p>
              <p>
                We don't allow hateful conduct or the use of hate speech while on Discord. This
                includes the use of hate symbols and the denial of historical mass atrocities. We
                define <strong>hate speech</strong> to include any expression that degrades,
                vilifies, or dehumanizes individuals, incites hostility towards defined groups, or
                promotes harm based on protected characteristics.
              </p>
              <p>
                <strong>Protected characteristics</strong> at Discord encompass various aspects,
                such as age; caste; color; disability; ethnicity; family responsibilities; gender
                and gender identity; housing status; national origin; race; refugee or immigration
                status; religious affiliation; serious illness; sex; sexual orientation;
                socioeconomic status; source of income; status as a victim of domestic violence,
                sexual violence, or stalking; as well as weight and size.
              </p>
              <p>
                However, we understand that there are nuances and may grant exceptions in instances
                of reclaimed language, satire, educational or documentary purposes. The use of
                satire must be obvious and we will not allow users to deflect blame retroactively by
                claiming their statements were made ironically or as a joke.
              </p>
            </section>

            <section id="hate-symbols" className={styles.section}>
              <h2>
                <strong>Hate Symbols and Imagery</strong>
              </h2>
              <p>
                The use or sharing of hate symbols or imagery is strictly prohibited on Discord.
                This policy extends to all images uploaded or shared on Discord, including profiles,
                channels, servers and apps.
              </p>
              <p>
                Hate symbols include acronyms, numbers, phrases, logos, flags, gestures and other
                miscellaneous symbols used to promote or incite hatred, threats, discrimination, or
                violence against other people on the basis of protected characteristics. Symbols
                that represent supremacy of one group over another are also prohibited under this
                policy.
              </p>
              <p>
                However, exceptions may be made in instances where hate symbols are shared for
                educational, historical, newsworthy, or culturally appropriate and non-hateful
                purposes.
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setHateSymbolsExamplesOpen(!hateSymbolsExamplesOpen)}
                aria-expanded={hateSymbolsExamplesOpen}
                type="button"
              >
                <span>For example, you may not post, share, or engage in:</span>
                <span className={styles.dropdownIcon}>{hateSymbolsExamplesOpen ? '−' : '+'}</span>
              </button>

              {hateSymbolsExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <p>Sharing any hate symbol or imagery with any of the following intentions:</p>
                  <ul>
                    <li>
                      To target an individual, group, or server based on their protected
                      characteristic(s) as a way to harass, threaten, or harm them
                    </li>
                    <li>
                      To incite fear, hatred, discrimination, or violence against a protected group
                    </li>
                    <li>To represent or claim the supremacy of one protected group over another</li>
                    <li>
                      Glorify violence against individuals or groups based on their protected
                      characteristics
                    </li>
                    <li>Deny the existence of a violent event (such as the Holocaust)</li>
                  </ul>
                </div>
              )}
            </section>

            <section id="how-to-avoid" className={styles.section}>
              <h2>
                <strong>How to Avoid Violating These Policies</strong>
              </h2>
              <p>
                Please report and leave servers dedicated to the violative content described above.
                Discord may also take action against accounts that are present in violative servers.
              </p>
              <ul>
                <li>
                  If you are not sure whether a piece of content might violate these policies, don't
                  post it
                </li>
              </ul>
              <p>
                If you find yourself in a server that is sharing this type of content,{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  report
                </a>{' '}
                it and consider removing yourself from the space
              </p>
            </section>

            <section id="how-to-report" className={styles.section}>
              <h2>
                <strong>How to Report Violations of These Policies</strong>
              </h2>
              <p>
                Reporting safety violations is critically important to keeping you and the broader
                Discord community safe. All Discord users can report policy violations right in the
                app by following the instructions{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                . For more information on these policies, please reference our{' '}
                <a href="https://discord.com/guidelines" target="_blank" rel="noopener noreferrer">
                  Community Guidelines
                </a>{' '}
                <GuidelineTooltip guidelineNumber="4" language={language}>
                  <a href="/discord/guidelines#guideline-4" className={styles.guidelineLink}>
                    #4
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
            <section id="resources" className={styles.section}>
              <h2>Resources</h2>
              <p>
                If you or another user is in danger, please contact law enforcement right away and
                let them know what is happening, regardless of the information you can provide.
              </p>
            </section>
          </>
        )}
      </div>

      {/* Tags section */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Bezpečnost uživatelů' : 'User Safety'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordHatefulConductPolicyPage
